/**
 * TranslatePageButton — manual per-page toggle for Google Translate.
 *
 * Drops a small "Translate this page" button (or "Show original" when
 * already translated) anywhere on a page. Picks the language from the
 * user's current portal language; falls back to a dropdown when the
 * portal language is one Google doesn't support (e.g. Ndebele).
 *
 * Usage:
 *   <TranslatePageButton />                 // uses current i18n language
 *   <TranslatePageButton lang="fr" />       // forces French
 *
 * Companion to the global LanguageSwitcher — both call the same helper
 * (lib/googleTranslate.js).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Languages, Globe2, X } from 'lucide-react';
import {
  setGoogleTranslateLanguage,
  clearGoogleTranslate,
  getCurrentGoogleTranslateLang,
  isGoogleTranslateSupported,
} from '@/lib/googleTranslate';
import { SUPPORTED_LANGUAGES } from '@/i18n';

const TranslatePageButton = ({ lang, variant = 'outline', size = 'sm', className = '' }) => {
  const { i18n, t } = useTranslation();
  const portalLang = i18n.resolvedLanguage || i18n.language || 'en';
  const targetLang = lang || portalLang;
  const [current, setCurrent] = useState(getCurrentGoogleTranslateLang());

  useEffect(() => {
    const id = setInterval(() => setCurrent(getCurrentGoogleTranslateLang()), 1000);
    return () => clearInterval(id);
  }, []);

  const isTranslated = current && current !== 'en';

  const translate = (code) => {
    setGoogleTranslateLanguage(code);
    setCurrent(code);
  };

  const reset = () => {
    clearGoogleTranslate();
    setCurrent('en');
  };

  // If currently translated → show "Show original"
  if (isTranslated) {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={reset}
        className={`gap-2 ${className}`}
        data-testid="translate-page-reset"
        title={t('translate.showOriginal', 'Show original (English)')}
      >
        <X className="h-4 w-4" />
        <span className="hidden sm:inline">
          {t('translate.showOriginal', 'Show original')}
        </span>
      </Button>
    );
  }

  // If portal language is supported by Google → one-click translate
  if (isGoogleTranslateSupported(targetLang) && targetLang !== 'en') {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={() => translate(targetLang)}
        className={`gap-2 ${className}`}
        data-testid="translate-page-btn"
        title={t('translate.translatePage', 'Translate this page')}
      >
        <Languages className="h-4 w-4" />
        <span className="hidden sm:inline">
          {t('translate.translatePage', 'Translate this page')}
        </span>
      </Button>
    );
  }

  // Otherwise show a dropdown so user can pick
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`gap-2 ${className}`}
          data-testid="translate-page-trigger"
        >
          <Globe2 className="h-4 w-4" />
          <span className="hidden sm:inline">
            {t('translate.translatePage', 'Translate this page')}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {SUPPORTED_LANGUAGES
          .filter(l => isGoogleTranslateSupported(l.code))
          .map((l) => (
            <DropdownMenuItem
              key={l.code}
              onClick={() => translate(l.code)}
              data-testid={`translate-page-option-${l.code}`}
              className="cursor-pointer"
            >
              <span className="text-lg mr-2">{l.flag}</span>
              <span className="font-medium">{l.nativeName}</span>
              <span className="ml-auto text-xs text-muted-foreground">{l.name}</span>
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { TranslatePageButton };
export default TranslatePageButton;
